import { useCallback, useEffect, useState } from "react";
import {
  useFetcher,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import { unpackObjForSubmit, packObjForSubmit } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { IContactEmailData, IContactAddressData } from "./ContactAddressData";
import { defaultData, ProgressBarSteps } from "./ContactAddressModels";
import { doFetchPut } from "../../utils/doFetchPut";
import { ContactAddress } from "./ContactAddress";

export const contactAddressRouteLoader = async ({
  request,
}: {
  request: any;
  params: any;
}) => {
  return doFetch(
    `my-profile-contact-address`,
    { signal: request.signal },
    true,
    true
  );
};

export const addressPredictionsLoader = async ({
  request,
}: { 
  request: any;
}) => {
  const url = new URL(request.url);
  const query = url.searchParams.get("addressQuery");
  const lastId = url.searchParams.get("lastId");

  let fetchUrl = `address-validate/predictions?addressQuery=${encodeURIComponent(query || "")}`;
  if (lastId) {
    fetchUrl += `&lastId=${lastId}`;
  }
  
  return doFetch(fetchUrl, { signal: request.signal });
}

export const addressDetailsLoader = async ({ 
  request,
}: { 
  request: any;
 }) => {
  const url = new URL(request.url);
  const addressId = url.searchParams.get("addressId");

  let fetchURL = `address-validate/details?addressId=${addressId}`;

  return doFetch(fetchURL, { signal: request.signal });
};

export const contactEmailRouteAction = async ({
  request,
}: {
  request: any;
  params: any;
}) => {
  let formData = await request.formData();
  let obj = JSON.parse(unpackObjForSubmit(formData));
  return doFetchPut(
    "my-profile-contact-email",
    { signal: request.signal },
    obj
  );
};

export const contactAddressRouteAction = async ({
  request,
}: {
  request: any;
  params: any;
}) => {
  let formData = await request.formData();
  let obj = JSON.parse(unpackObjForSubmit(formData));
  return doFetchPut(
    "my-profile-contact-address",
    { signal: request.signal },
    obj
  );
};

export const ContactAddressRoute = () => {
  const submitFetcher = useFetcher();
  const predictionsFetcher = useFetcher();
  const detailsFetcher = useFetcher();
  const data = useLoaderData() as IContactEmailData;
  const navigate = useNavigate();

  const [currentData, setCurrentData] = useState<IContactEmailData>({
    ...defaultData,
    ...data,
    employeeAddress: {
      ...defaultData.employeeAddress,
      ...data.employeeAddress,
    }
  });

  const [activeStep, setActiveStep] = useState<ProgressBarSteps>(
    ProgressBarSteps.VerifyEmail
  );
  const [error, setError] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isAddressFormTouched, setAddressFormTouched] = useState(false);
  const [hasVerifiedEmail, setHasVerifiedEmail] = useState(!!data.employeeEmail);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [validatedAddress, setValidatedAddress] = useState(false);
  const [isAddressFormDisabled, setAddressFormDisabled] = useState(false);
  const [lastValidatedAddress, setLastValidatedAddress] = useState<{
    addressLine1: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  } | null>(null);

  const { employeeAddress } = data;

  const isAddressValid = useCallback(() => {
    if (!lastValidatedAddress) return false;
    return (
      currentData.employeeAddress.addressLine1 === lastValidatedAddress.addressLine1 &&
      currentData.employeeAddress.city === lastValidatedAddress.city &&
      currentData.employeeAddress.province === lastValidatedAddress.province &&
      currentData.employeeAddress.postalCode === lastValidatedAddress.postalCode &&
      currentData.employeeAddress.country === lastValidatedAddress.country
    );
  }, [currentData, lastValidatedAddress]);

  useEffect(() => {
    if (lastValidatedAddress) {
      setValidatedAddress(isAddressValid());
    }
  }, [currentData.employeeAddress, lastValidatedAddress, isAddressValid]);

  const isAddressSameAsStored = () => {
    return (
      employeeAddress.addressLine1 === currentData.employeeAddress.addressLine1 &&
      employeeAddress.city === currentData.employeeAddress.city &&
      employeeAddress.province === currentData.employeeAddress.province &&
      employeeAddress.postalCode === currentData.employeeAddress.postalCode &&
      employeeAddress.country === currentData.employeeAddress.country
    );
  }

  const onChangeField = (field: keyof IContactAddressData, value: string) => {
    if (!isAddressFormTouched && field != "addressLine2") {
      setAddressFormTouched(true);
    }

    setCurrentData((prev) => ({
      ...prev,
      employeeAddress: {
        ...prev.employeeAddress,
        [field]: value,
      },
    }));
  };

  const onSubmit = () => {
    if (!validatedAddress 
      || currentData.employeeAddress.country !== "Canada") {
      setError(true);
      setAddressFormDisabled(true);
      return;
    }

    const submittedAddress = currentData.employeeAddress;
    if (
      submittedAddress.addressLine1.trim().length > 30
      || (submittedAddress.addressLine2 && submittedAddress.addressLine2.trim().length > 30)
      || submittedAddress.city.trim().length > 30
      || submittedAddress.province.trim().length > 2
      || submittedAddress.postalCode.trim().length > 10
      || submittedAddress.country.trim().length > 6
    ) {
      setError(true);
      setAddressFormDisabled(true);
      return;
    }

    const addressFormData = {
      addressLine1: currentData.employeeAddress.addressLine1.trim(),
      addressLine2: currentData.employeeAddress.addressLine2?.trim() || "",
      city: currentData.employeeAddress.city.trim(),
      province: currentData.employeeAddress.province.trim(),
      postalCode: currentData.employeeAddress.postalCode.trim(),
      country: currentData.employeeAddress.country.trim(),
    }

    submitFetcher.submit(packObjForSubmit(addressFormData), { method: "put" });

    const currentIndex = Object.values(ProgressBarSteps).indexOf(activeStep);
    if (currentIndex < Object.values(ProgressBarSteps).length - 1) {
      setActiveStep(Object.values(ProgressBarSteps)[currentIndex + 1]);
    }
  };

  const onSubmitEmail = async () => {
    const email = currentData.employeeEmail.trim();
    const formData = packObjForSubmit({ email }); 
    submitFetcher.submit(formData, { method: "put", action: "/contact-address/verify-email" });
  };

  useEffect(() => {
    if (submitFetcher.data) {
      if (submitFetcher.data.success) {
        setSubmissionSuccess(true);
      } else {
        setSubmissionSuccess(false);
      }
    }
  }, [submitFetcher.data]);

  const isValidEmail = (value: string) => {
    const emailRegex =
      /^[A-Z0-9](?:[A-Z0-9._%+-]{0,62}[A-Z0-9])?@([A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+[A-Z]{2,63}$/i;
    return emailRegex.test(value);
  };

  const onSetEmail = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentData(prev => ({
      ...prev,
      employeeEmail: value,
    }))
    setIsFormTouched(true);
  };

  const onNext = () => {
    const currentIndex = Object.values(ProgressBarSteps).indexOf(activeStep);
    if (currentIndex < Object.values(ProgressBarSteps).length - 1) {
      setActiveStep(Object.values(ProgressBarSteps)[currentIndex + 1]);
    }
  };

  return (
    <ContactAddress
      data={currentData}
      submitting={submitFetcher.state === "submitting"}
      predictionsFetcher={predictionsFetcher}
      detailsFetcher={detailsFetcher}
      activeStep={activeStep}
      onChangeField={onChangeField}
      email={currentData.employeeEmail}
      onSubmit={onSubmit}
      error={error}
      setError={setError}
      setAddressFormDisabled={setAddressFormDisabled}
      onNext={onNext}
      isNextDisabled={isNextDisabled}
      isFormTouched={isFormTouched}
      isAddressFormTouched={isAddressFormTouched}
      isAddressFormDisabled={isAddressFormDisabled}
      submissionSuccess={submissionSuccess}
      validatedAddress={validatedAddress}
      isAddressValid={isAddressValid}
      isAddressSameAsStored={isAddressSameAsStored}
      setLastValidatedAddress={setLastValidatedAddress}
      setValidatedAddress={setValidatedAddress}
      hasVerifiedEmail={hasVerifiedEmail}
      isValidEmail={isValidEmail}
      onSetEmail={onSetEmail}
      onSubmitEmail={onSubmitEmail}
      onClose={() => navigate("/my-profile")}
    />
  );
};
